import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import DashboardToolbar from '../components/dashboard/DashboardToolbar';
import DashboardKpiCard from '../components/dashboard/DashboardKpiCard';
import { useWarrantyData } from '../hooks/useDashboardData';
import {
  ALL_MONTHS, getYearsFromYearMonth,
  getPeriodLabel, getComparisonPeriod, buildKpiComparison, sumByProduct,
  calcRejectionRate, avgRejectionFromRecords, getDefaultDashboardYear, getDefaultDashboardPeriod,
  getDashboardDefaultFilters,
  collectPeriodKeysFromYearMonth, filterPeriodsByYear, filterYearMonthDashboard,
  filterYearMonthRecords, filterYearMonthRecordsMulti, toFilterArray, ALL_YEARS, isNoSelection,
} from '../utils/dashboardUtils';
import { exportWarranty } from '../utils/dashboardExport';
import {
  ProductSupplyBarChart, ProductWarrantyBarChart, ProductRejectionLineChart,
  Top5RejectionBarChart, SupplyGrowthComboChart, WarrantyGrowthComboChart,
  RejectionByProductPeriodChart,
} from '../components/dashboard/DashboardCharts';

const Panel = ({ title, children, colors, span = 3, chartMinHeight = 220 }) => (
  <Box
    gridColumn={`span ${span}`}
    gridRow="span 2"
    bgcolor={colors.primary[400]}
    p="10px"
    display="flex"
    flexDirection="column"
    overflow="hidden"
  >
    <Typography color={colors.grey[100]} variant="subtitle2" fontWeight={600} mb={0.5} flexShrink={0}>
      {title}
    </Typography>
    <Box sx={{ position: 'relative', flex: 1, minHeight: chartMinHeight, width: '100%' }}>{children}</Box>
  </Box>
);

const prunePeriods = (selected, available) => {
  if (isNoSelection(selected)) return selected;
  const valid = selected.filter((p) => available.includes(p));
  if (valid.length) return valid;
  const def = getDefaultDashboardPeriod();
  if (available.includes(def)) return [def];
  return available.length ? [available[available.length - 1]] : [];
};

const WarrantyDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { supplyRows, warrantyRows, rejectionRows, loading, error, refresh } = useWarrantyData();
  const [years, setYears] = useState(() => [getDefaultDashboardYear()]);
  const [periods, setPeriods] = useState(() => [getDefaultDashboardPeriod()]);
  const [products, setProducts] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const allRecords = useMemo(() => [...supplyRows, ...warrantyRows, ...rejectionRows], [supplyRows, warrantyRows, rejectionRows]);
  const yearList = useMemo(() => getYearsFromYearMonth(allRecords), [allRecords]);
  const yearOptions = useMemo(() => yearList.map((y) => ({ value: String(y), label: String(y) })), [yearList]);
  const allPeriods = useMemo(() => collectPeriodKeysFromYearMonth(allRecords), [allRecords]);
  const periodKeys = useMemo(() => filterPeriodsByYear(allPeriods, years, 'calendar'), [allPeriods, years]);
  const periodOptions = useMemo(() => periodKeys.map((p) => ({ value: p, label: p })), [periodKeys]);
  const cmp = useMemo(() => getComparisonPeriod(years, periods), [years, periods]);

  const handleYearsChange = useCallback((newYears) => {
    setYears(newYears);
    const next = filterPeriodsByYear(allPeriods, newYears, 'calendar');
    setPeriods((prev) => prunePeriods(prev, next));
  }, [allPeriods]);

  useEffect(() => {
    setPeriods((prev) => {
      const valid = prev.filter((p) => periodKeys.includes(p));
      if (valid.length) return valid.length === prev.length ? prev : valid;
      return prunePeriods(prev, periodKeys);
    });
  }, [periodKeys]);

  const yearSupply = useMemo(() => {
    const yearArr = toFilterArray(years, ALL_YEARS);
    if (yearArr.length) return filterYearMonthRecordsMulti(supplyRows, years);
    return filterYearMonthRecords(supplyRows, cmp.currentYear, ALL_MONTHS);
  }, [supplyRows, years, cmp.currentYear]);
  const yearWarranty = useMemo(() => {
    const yearArr = toFilterArray(years, ALL_YEARS);
    if (yearArr.length) return filterYearMonthRecordsMulti(warrantyRows, years);
    return filterYearMonthRecords(warrantyRows, cmp.currentYear, ALL_MONTHS);
  }, [warrantyRows, years, cmp.currentYear]);

  const currSupply = useMemo(() => filterYearMonthRecords(supplyRows, cmp.currentYear, cmp.currentMonth), [supplyRows, cmp]);
  const prevSupply = useMemo(() => filterYearMonthRecords(supplyRows, cmp.prevYear, cmp.prevMonth), [supplyRows, cmp]);
  const currWarranty = useMemo(() => filterYearMonthRecords(warrantyRows, cmp.currentYear, cmp.currentMonth), [warrantyRows, cmp]);
  const prevWarranty = useMemo(() => filterYearMonthRecords(warrantyRows, cmp.prevYear, cmp.prevMonth), [warrantyRows, cmp]);
  const currRejection = useMemo(() => filterYearMonthRecords(rejectionRows, cmp.currentYear, cmp.currentMonth), [rejectionRows, cmp]);
  const prevRejection = useMemo(() => filterYearMonthRecords(rejectionRows, cmp.prevYear, cmp.prevMonth), [rejectionRows, cmp]);

  const filteredSupply = useMemo(() => filterYearMonthDashboard(supplyRows, years, periods, 'calendar'), [supplyRows, years, periods]);
  const filteredWarranty = useMemo(() => filterYearMonthDashboard(warrantyRows, years, periods, 'calendar'), [warrantyRows, years, periods]);
  const filteredRejection = useMemo(() => filterYearMonthDashboard(rejectionRows, years, periods, 'calendar'), [rejectionRows, years, periods]);
  const periodLabel = getPeriodLabel(years, periods);

  const yearSupplyTotal = sumByProduct(yearSupply, products);
  const yearWarrantyTotal = sumByProduct(yearWarranty, products);
  const yearRejRate = calcRejectionRate(yearSupplyTotal, yearWarrantyTotal);

  const mtdSupply = buildKpiComparison(sumByProduct(currSupply, products), sumByProduct(prevSupply, products));
  const mtdWarranty = buildKpiComparison(sumByProduct(currWarranty, products), sumByProduct(prevWarranty, products));
  const mtdRejCurr = calcRejectionRate(sumByProduct(currSupply, products), sumByProduct(currWarranty, products))
    || avgRejectionFromRecords(currRejection, products);
  const mtdRejPrev = calcRejectionRate(sumByProduct(prevSupply, products), sumByProduct(prevWarranty, products))
    || avgRejectionFromRecords(prevRejection, products);
  const mtdRej = buildKpiComparison(mtdRejCurr, mtdRejPrev);

  const kpis = [
    { label: 'Supply Quantity (Year)', value: yearSupplyTotal },
    { label: 'Warranty Quantity (Year)', value: yearWarrantyTotal },
    { label: 'Rejection Rate (Year)', value: yearRejRate },
    { label: 'Supply Quantity (MTD)', value: mtdSupply.current },
    { label: 'Warranty Quantity (MTD)', value: mtdWarranty.current },
    { label: 'Rejection Rate (MTD)', value: mtdRej.current },
  ];

  const handleDownload = async () => {
    setDownloading(true);
    try { await exportWarranty(filteredSupply, filteredWarranty, filteredRejection, periodLabel, kpis); }
    finally { setDownloading(false); }
  };

  const handleRefresh = useCallback(() => {
    const defaults = getDashboardDefaultFilters('warranty');
    setYears(defaults.years);
    setPeriods(defaults.periods);
    setProducts(defaults.products);
    refresh();
  }, [refresh]);

  if (error) {
    return (
      <Box ml="200px" mt="60px" p={2} width="calc(100% - 220px)">
        <Typography color="error">Failed to load warranty data: {error}</Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Ensure MIS backend is running at {process.env.REACT_APP_MISBACKEND_URL}
        </Typography>
      </Box>
    );
  }

  return (
    <Box ml="200px" mt={0} pl={2} width="calc(100% - 220px)">
      <DashboardToolbar
        variant="warranty"
        title="Supply Vs Rejection Overview"
        subtitle="Supply and Rejections Summary View"
        years={years} periods={periods} yearOptions={yearOptions} periodOptions={periodOptions}
        onYearsChange={handleYearsChange} onPeriodsChange={setPeriods}
        periodLabel={periodLabel}
        products={products} onProductsChange={setProducts}
        onDownload={handleDownload} onRefresh={handleRefresh}
        downloading={downloading} loading={loading}
      />
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gridAutoRows="minmax(110px, auto)" gap="5px">
          <Box gridColumn="span 2"><DashboardKpiCard label="Supply Quantity (Year)" value={yearSupplyTotal} /></Box>
          <Box gridColumn="span 2"><DashboardKpiCard label="Warranty Quantity (Year)" value={yearWarrantyTotal} /></Box>
          <Box gridColumn="span 2"><DashboardKpiCard label="Rejection Rate (Year)" value={yearRejRate} format="percent" isPercent /></Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Supply Quantity (MTD)" value={mtdSupply.current}
              prevLabel={cmp.prevLabel} prevValue={mtdSupply.previous} pct={mtdSupply.pct} increased={mtdSupply.increased} />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Warranty Quantity (MTD)" value={mtdWarranty.current}
              prevLabel={cmp.prevLabel} prevValue={mtdWarranty.previous} pct={mtdWarranty.pct} increased={mtdWarranty.increased} />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Rejection Rate (MTD)" value={mtdRej.current} format="percent" isPercent
              prevLabel={cmp.prevLabel} prevValue={mtdRej.previous} pct={mtdRej.pct} increased={mtdRej.increased} />
          </Box>

          <Panel title="Total Supply By Product" colors={colors} span={3} chartMinHeight={250}>
            <ProductSupplyBarChart records={filteredSupply} product={products} />
          </Panel>
          <Panel title="Total Warranty By Product" colors={colors} span={3} chartMinHeight={250}>
            <ProductWarrantyBarChart records={filteredWarranty} product={products} />
          </Panel>
          <Panel title="Rejection Rate" colors={colors} span={3} chartMinHeight={250}>
            <ProductRejectionLineChart supply={filteredSupply} warranty={filteredWarranty} product={products} />
          </Panel>
          <Panel title="Top 5 Products by Rejection Rate" colors={colors} span={3}>
            <Top5RejectionBarChart supply={filteredSupply} warranty={filteredWarranty} product={products} />
          </Panel>

          <Panel title="Supply and Growth (%) Over Period" colors={colors} span={4}>
            <SupplyGrowthComboChart records={filteredSupply} product={products} />
          </Panel>
          <Panel title="Warranty and Growth (%) Over Period" colors={colors} span={4}>
            <WarrantyGrowthComboChart supply={filteredSupply} warranty={filteredWarranty} product={products} />
          </Panel>
          <Panel title="Rejection Rate by Product Over Period" colors={colors} span={4}>
            <RejectionByProductPeriodChart records={filteredRejection} product={products} />
          </Panel>
        </Box>
      )}
    </Box>
  );
};

export default WarrantyDashboard;
